import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations as baseTranslations } from './translations';
import { moreTranslations } from './translationsMore';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = ['en', 'it', 'es', 'fr', 'pt'];

// Joins the original translations with the extra ones in translationsMore.js, section by section
function mergeDeep(base, extra) {
    if (!extra) return base;
    const result = { ...base };
    Object.keys(extra).forEach(key => {
        const a = base ? base[key] : undefined;
        const b = extra[key];
        const bothObjects = a && b && typeof a === 'object' && typeof b === 'object' && !Array.isArray(a) && !Array.isArray(b);
        result[key] = bothObjects ? mergeDeep(a, b) : b;
    });
    return result;
}

const translations = {};
Object.keys(baseTranslations).forEach(lang => {
    translations[lang] = mergeDeep(baseTranslations[lang], moreTranslations[lang]);
});
Object.keys(moreTranslations).forEach(lang => {
    if (!translations[lang]) translations[lang] = moreTranslations[lang];
});

export function LanguageProvider({ children }) {
    const [interfaceLanguage, setInterfaceLanguageState] = useState(() => {
        const stored = localStorage.getItem('vozoly_interface_language');
        return SUPPORTED_LANGUAGES.includes(stored) ? stored : 'en';
    });

    const setInterfaceLanguage = (lang) => {
        if (!SUPPORTED_LANGUAGES.includes(lang)) return;
        localStorage.setItem('vozoly_interface_language', lang);
        setInterfaceLanguageState(lang);
    };

    useEffect(() => {
        document.documentElement.lang = interfaceLanguage;
    }, [interfaceLanguage]);

    const t = (key) => {
        const parts = key.split('.');
        let current = translations[interfaceLanguage] || translations.en;
        for (const part of parts) {
            current = current?.[part];
            if (current === undefined) break;
        }
        if (current === undefined) {
            let fallback = translations.en;
            for (const part of parts) {
                fallback = fallback?.[part];
            }
            return fallback !== undefined ? fallback : key;
        }
        return current;
    };

    return (
        <LanguageContext.Provider value={{ interfaceLanguage, setInterfaceLanguage, t, SUPPORTED_LANGUAGES }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
    return context;
}